/**
 * @file src/api/payment.api.js
 * @description Sales-side Payment API calls.
 *
 * Rules:
 * - API layer only.
 * - No React.
 * - No hooks.
 * - No state.
 * - No mock data.
 * - No companyId is sent from frontend.
 * - Backend resolves company from selected company token/context.
 */

import { apiClient } from "lib/axios";

const normalizePayment = (payment) => {
  if (!payment) return payment;

  return {
    ...payment,
    id: payment.id || payment._id,
    clientId:
      payment.clientId?._id ||
      payment.clientId?.id ||
      payment.clientId ||
      payment.client?._id ||
      payment.client?.id ||
      "",
    clientName:
      payment.clientName ||
      payment.clientId?.name ||
      payment.client?.name ||
      payment.clientId?.clientName ||
      payment.client?.clientName ||
      "—",
  };
};

const normalizePaymentListResponse = (data) => {
  const payments = data?.payments || data?.data?.payments || data?.data || [];

  return {
    ...data,
    payments: Array.isArray(payments) ? payments.map(normalizePayment) : [],
  };
};

const normalizePaymentResponse = (data) => {
  const payment = data?.payment || data?.data?.payment || data?.data || data;

  return {
    ...data,
    payment: normalizePayment(payment),
  };
};

const buildCleanPayload = (payload) => ({
  clientId: payload.clientId,
  branchId: payload.branchId || null,
  paymentDate: payload.paymentDate,
  paymentMode: payload.paymentMode,
  amount: payload.amount,
  allocations: Array.isArray(payload.allocations) ? payload.allocations : [],
  depositTo: payload.depositTo || null,
  issuer: payload.issuer || null,
  reference: payload.reference || null,
  comments: payload.comments || null,
});

export const getPayments = async (params = {}) => {
  const { data } = await apiClient.get("/payments", {
    params,
  });

  return normalizePaymentListResponse(data);
};

export const getPaymentById = async (id) => {
  const { data } = await apiClient.get(`/payments/${id}`);

  return normalizePaymentResponse(data);
};

export const createPayment = async (payload) => {
  const { data } = await apiClient.post(
    "/payments",
    buildCleanPayload(payload),
  );

  return normalizePaymentResponse(data);
};

export const updatePayment = async (id, payload) => {
  const { data } = await apiClient.put(
    `/payments/${id}`,
    buildCleanPayload(payload),
  );

  return normalizePaymentResponse(data);
};

export const deletePayment = async (id) => {
  const { data } = await apiClient.delete(`/payments/${id}`);

  return data;
};

export const voidPayment = async (id, body = {}) => {
  const { data } = await apiClient.post(`/payments/${id}/void`, body);
  return normalizePaymentResponse(data);
};
